import { useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";

export function useOrderStatusHandlers(
  selectedOrder: SalesRequest,
  rejectionReason: string,
  onOrderStatusChange?: () => void,
  setIsApproveSheetOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  setIsRejectSheetOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  customReason?: string,
  onCloseOrderDialog?: () => void
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const profileStore = useProfileStore();

  const handleApprove = async () => {
    if (!selectedOrder) return;

    // Role-based permission checks
    const userRole = profileStore.role;

    // Check permissions for payment validation
    if (selectedOrder.status === "payment_to_check") {
      if (userRole !== "cobranza" && userRole !== "superadmin") {
        toast({
          title: "Sin permisos",
          description:
            "Solo el equipo de cobranza o superadministradores pueden validar pagos",
          variant: "destructive",
        });
        return;
      }
    }

    // Pricing authorization is now handled by PricingApprovalActions component
    if (selectedOrder.status === "pending_pricing") {
      toast({
        title: "Error",
        description:
          "La autorización de precio debe hacerse a través del componente específico",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      let newStatus = "accepted" as const;

      // NEW WORKFLOW LOGIC
      // This hook now only handles final approval steps
      switch (selectedOrder.status) {
        case "pending":
          // From "Autorización cobranza" → Approved
          newStatus = "accepted";
          break;
        case "payment_to_check":
          // From "Validación pago" → Approved
          newStatus = "accepted";
          break;
        default:
          // All other statuses should use dedicated components
          newStatus = "accepted";
          break;
      }

      // Prepare update data
      const updateData: any = {
        status: newStatus,
        approved_rejected_by: profileStore.id,
        updated_at: new Date().toISOString(),
      };

      // Update the sales request status
      const { error } = await supabase
        .from("sales_request")
        .update(updateData)
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Status history is recorded by the record_sales_request_status_change
      // trigger on the UPDATE above (no manual insert -> no duplicate row).

      // Single consolidated toast notification
      const toastMessage = "La nota de venta ha sido aprobada exitosamente";

      toast({
        title: "Nota de venta aprobada",
        description: toastMessage,
        variant: "default",
      });

      if (onOrderStatusChange) onOrderStatusChange();
      if (setIsApproveSheetOpen) setIsApproveSheetOpen(false);
      // Return to the board so the approved card animates into its new column.
      onCloseOrderDialog?.();
    } catch (error) {
      console.error("Error approving order:", error);
      toast({
        title: "Error",
        description: "No se pudo aprobar la nota de venta",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selectedOrder) return;
    if (!rejectionReason.trim()) {
      toast({
        title: "Error",
        description: "Debe proporcionar un motivo para rechazar la nota",
        variant: "destructive",
      });
      return;
    }

    // Use custom reason if "Otro motivo" is selected and custom reason is provided
    const finalRejectionReason =
      rejectionReason === "Otro motivo" && customReason?.trim()
        ? customReason.trim()
        : rejectionReason;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "rejected",
          approved_rejected_by: profileStore.id,
          rejected_reason: finalRejectionReason,
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Status history is recorded by the record_sales_request_status_change
      // trigger on the UPDATE above (no manual insert -> no duplicate row).

      toast({
        title: "Nota rechazada",
        description: "La nota de venta ha sido rechazada correctamente",
        variant: "default",
      });

      if (onOrderStatusChange) onOrderStatusChange();
      if (setIsRejectSheetOpen) setIsRejectSheetOpen(false);
      onCloseOrderDialog?.();
    } catch (error) {
      console.error("Error rejecting order:", error);
      toast({
        title: "Error",
        description: "No se pudo rechazar la nota de venta",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegisterPayment = async () => {
    if (!selectedOrder) return;

    // Sellers can register payments regardless of sales condition

    setIsSubmitting(true);

    try {
      // Update status from "prepaid" to "payment_to_check"
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "payment_to_check",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Trigger logs the change; just attach the reason to that row.
      await supabase.rpc("annotate_latest_status_change", {
        p_sales_request_id: selectedOrder.id,
        p_new_status: "payment_to_check",
        p_comment: "Pago registrado por vendedor",
      });

      toast({
        title: "Pago registrado",
        description:
          "El pago ha sido registrado. Pendiente de validación por cobranza.",
      });

      if (onOrderStatusChange) onOrderStatusChange();
    } catch (error) {
      console.error("Error registering payment:", error);
      toast({
        title: "Error",
        description: "No se pudo registrar el pago",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    isSubmitting,
    handleApprove,
    handleReject,
    handleRegisterPayment,
  };
}
